import { getMailboxService } from "../services/mailbox-service";
import { IAttachment, ICategory, IMailItem } from "./mail-item-types";

export const wrapMailItem = async (
  mailbox: Office.Mailbox | undefined,
  productCategory: ICategory
): Promise<IMailItem | undefined> => {
  const officeItem = mailbox?.item;

  if (!officeItem) {
    return undefined;
  }

  const composeItem = officeItem as Office.MessageCompose;

  const isComposeItem = (): boolean => {
    // This is a hack to check if the mail item is from compose.
    // The compose item has an itemIdAsync method that returns a promise.
    // The read item does not have this method. P.s, Microsoft, why?
    return composeItem && composeItem.getItemIdAsync !== undefined;
  };

  const { from, to, internetMessageId, conversationId, itemType, cc, subject } = officeItem;

  const getCustomProperty = async (propertyName: string) => {
    return getMailboxService().getMailItemProperty(officeItem, propertyName);
  };

  const setCustomProperty = async (propertyName: string, value: string) => {
    return getMailboxService().setMailItemProperty(officeItem, propertyName, value);
  };

  // Callbacks are yuck for me...
  const getAttachments = async (): Promise<IAttachment[]> => {
    try {
      const readItem = officeItem as Office.MessageRead;

      if (readItem && readItem.attachments) {
        return readItem.attachments.map((attachmentDetail: Office.AttachmentDetailsCompose) => ({
          id: attachmentDetail.id,
          url: attachmentDetail.url,
          name: attachmentDetail.name,
          size: attachmentDetail.size,
          isInline: attachmentDetail.isInline,
          attachmentType: attachmentDetail.attachmentType as "file" | "item",
        }));
      }

      if (!composeItem) {
        return [];
      }

      const result = await new Promise<Office.AsyncResult<Office.AttachmentDetailsCompose[]>>((resolve, reject) => {
        composeItem.getAttachmentsAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            reject(result.error);
          } else {
            resolve(result);
          }
        });
      });

      return result.value.map((attachmentDetail: Office.AttachmentDetailsCompose) => ({
        id: attachmentDetail.id,
        url: attachmentDetail.url,
        name: attachmentDetail.name,
        size: attachmentDetail.size,
        isInline: attachmentDetail.isInline,
        attachmentType: attachmentDetail.attachmentType as "file" | "item",
      }));
    } catch (error) {
      console.error("Error getting attachments:", error);
      return [];
    }
  };

  const getMailBoxCategory = async (categoryName: string): Promise<ICategory | null> => {
    try {
      if (!categoryName) {
        return null;
      }

      const result = await new Promise<Office.AsyncResult<Office.CategoryDetails[]>>((resolve, reject) => {
        mailbox.masterCategories.getAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            reject(result.error);
          } else {
            resolve(result);
          }
        });
      });

      const officeCategory = result.value.find((c: Office.CategoryDetails) => c.displayName === categoryName.trim());

      if (officeCategory) {
        return {
          displayName: officeCategory.displayName,
          color: officeCategory.color as Office.MailboxEnums.CategoryColor,
        };
      }

      return null;
    } catch (error) {
      console.error("Error getting mailbox category:", error);
      return null;
    }
  };

  const addMasterCategoryCategory = async (category: ICategory): Promise<ICategory> => {
    const masterCategory = await getMailBoxCategory(category.displayName);
    if (masterCategory) {
      return masterCategory;
    }

    return new Promise((resolve, reject) => {
      const officeCategory: Office.CategoryDetails = { ...productCategory };

      mailbox.masterCategories.addAsync([officeCategory], (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.error(result.error);
          reject(result.error);
        } else {
          resolve(category);
        }
      });
    });
  };

  const addProductCategory = async (): Promise<ICategory | null> => {
    try {
      const masterCategory = await addMasterCategoryCategory(productCategory);
      const mailItem = officeItem as Office.MessageRead | Office.MessageCompose;

      if (!mailItem) {
        console.error("Cannot set the category on null mail item.");
        return null;
      }

      if (!masterCategory) {
        return null;
      }

      const categoryName = masterCategory.displayName.trim();

      if (!categoryName) {
        throw new Error("The category requires a display name");
      }

      await new Promise((resolve, reject) => {
        mailItem.categories.addAsync([categoryName], (result) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            reject(result.error);
          } else {
            resolve(masterCategory);
          }
        });
      });

      return masterCategory;
    } catch (error) {
      console.error("Error adding category:", error);
      return null;
    }
  };

  const removeProductCategory = async (): Promise<boolean> => {
    try {
      const mailItem = officeItem as Office.MessageRead | Office.MessageCompose;

      const categoryName = productCategory.displayName.trim();

      if (!categoryName) {
        return true;
      }

      await new Promise((resolve, reject) => {
        mailItem.categories.removeAsync([categoryName], (result) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            console.error(result.error);
            reject(result.error);
            return;
          }
          resolve(true);
        });
      });

      return true;
    } catch (error) {
      console.error("Error removing category:", error);
      return false;
    }
  };

  const tryCloseCompose = async () => {
    if (isComposeItem()) {
      console.info("Attempting to close the composing mail item.");
      composeItem.close();
    }
  };

  const isFromCompose = isComposeItem();
  const itemId = isFromCompose ? await getMailboxService().getComposeMailItemId(composeItem) : mailbox?.item?.itemId;

  if (!itemId) {
    console.error("Cannot get item id for mail item. Both read and compose item ids are undefined.");
    return undefined;
  }

  return {
    itemId,
    from,
    to,
    cc,
    internetMessageId,
    conversationId,
    itemType,
    subject,
    isFromCompose,
    tryCloseCompose,
    getCustomProperty,
    setCustomProperty,
    getAttachments,
    addProductCategory,
    removeProductCategory,
  };
};
